
import React, { useEffect, useState } from 'react';
import { Card } from "@/components/ui/card";
import { Loader2, AlertTriangle, RefreshCw, Clock } from "lucide-react";
import { CurrentAffair } from './CurrentAffairs';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useIsMobile } from "@/hooks/use-mobile";

export interface CurrentAffairViewerProps {
  article: CurrentAffair;
}

// Update the CurrentAffair interface to include metadata and processing_status
interface ExtendedCurrentAffair extends CurrentAffair {
  metadata?: {
    enhanced?: boolean;
    [key: string]: any;
  };
  processing_status?: 'pending' | 'processing' | 'processed' | 'error';
}

const CurrentAffairViewer: React.FC<CurrentAffairViewerProps> = ({ article }) => {
  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [processingPhase, setProcessingPhase] = useState<'none' | 'pending' | 'basic' | 'enhanced'>('none');
  const { isMobile } = useIsMobile();
  
  // Cast to the extended interface that includes metadata
  const extendedArticle = article as ExtendedCurrentAffair;
  
  // For debugging
  useEffect(() => {
    console.log("CurrentAffairViewer rendered with article:", extendedArticle);
    if (extendedArticle) {
      console.log("Content type:", typeof extendedArticle.content);
      console.log("Content length:", extendedArticle?.content?.length || 0);
      console.log("Processing status:", extendedArticle?.processing_status || "unknown");
    } else {
      console.log("No article data provided");
    }
  }, [extendedArticle]);
  
  // Determine the processing phase based on article metadata and status
  useEffect(() => {
    if (!extendedArticle) {
      setProcessingPhase('none');
      return;
    }
    
    if (extendedArticle.processing_status === 'pending') {
      setProcessingPhase('pending');
    } else if (extendedArticle.processing_status === 'error') {
      setProcessingPhase('none');
    } else if (extendedArticle.content && 
               extendedArticle.content.includes("Error processing document")) {
      setProcessingPhase('none');
    } else if (extendedArticle.metadata?.enhanced === true) {
      setProcessingPhase('enhanced');
    } else if (extendedArticle.content && 
               extendedArticle.content.trim().length > 100 &&
               extendedArticle.processing_status === 'processing') {
      setProcessingPhase('basic');
    } else {
      setProcessingPhase('none');
    }
  }, [extendedArticle]);
  
  // Function to refresh current article content from database
  const refreshArticleContent = async () => {
    if (!extendedArticle?.id) return;
    
    try {
      setIsRefreshing(true);
      
      const { data, error } = await supabase
        .from('current_affairs')
        .select('*')
        .eq('id', extendedArticle.id)
        .single();
        
      if (error) throw error;
      
      // This doesn't update the parent component's state directly
      // It only refreshes our local view of the content status
      if (data) {
        const hasEnhancedContent = data.metadata?.enhanced === true;
        
        if (data.content && 
            data.content.trim().length > 100 && 
            !data.content.includes("Error processing document")) {
          
          setIsContentLoaded(true);
          setContentError(null);
          
          if (hasEnhancedContent) {
            setProcessingPhase('enhanced');
            toast.success("Content has been fully enhanced with advanced processing");
          } else if (data.processing_status === 'processing') {
            setProcessingPhase('basic');
            toast.info("Basic content is available. Enhanced processing is still in progress.");
          } else {
            setProcessingPhase('none');
          }
        } else if (data.processing_status === 'error') {
          setContentError("Error processing this document. Please try uploading it again.");
          setProcessingPhase('none');
        } else {
          setContentError("Content is still being processed. Please wait a moment and try again.");
          setProcessingPhase('pending');
        }
      }
    } catch (err) {
      console.error("Error refreshing content:", err);
      toast.error("Failed to refresh content");
    } finally {
      setIsRefreshing(false);
    }
  };
  
  // Safely check if content exists and is meaningful
  const hasValidContent = extendedArticle && 
    extendedArticle.content && 
    typeof extendedArticle.content === 'string' && 
    extendedArticle.content.trim().length > 10 && // More than just placeholder text
    extendedArticle.content !== "<p>Content is being processed</p>" &&
    !extendedArticle.content.includes("<p>Error processing document");
  
  // Add effect to check for document readiness
  useEffect(() => {
    // Reset states when article changes
    setIsContentLoaded(false);
    setContentError(null);
    
    // Simple guard clause to ensure article exists
    if (!extendedArticle) {
      setContentError("No article data available.");
      return;
    }
    
    try {
      if (hasValidContent) {
        // Content is available, display immediately
        console.log("Content is valid, displaying");
        setIsContentLoaded(true);
      } else if (!extendedArticle.id) {
        // No article selected
        console.log("No article ID found");
        setContentError("Please select an article to view its content.");
      } else if (extendedArticle.processing_status === 'error' || (extendedArticle.content && extendedArticle.content.includes("Error processing"))) {
        console.log("Article has processing error");
        setContentError("Error processing this document. Please try uploading it again.");
      } else if (extendedArticle.content === "<p>Content is being processed</p>" || extendedArticle.processing_status === 'pending') {
        console.log("Article is still processing");
        setContentError("This document is still being processed. Please wait a moment and refresh.");
      } else if (!extendedArticle.content || extendedArticle.content.trim() === '') {
        console.log("No content in article");
        setContentError("No content available for this article.");
      } else {
        // Attempt to display whatever content is available
        console.log("Displaying available content");
        setIsContentLoaded(true);
      }
    } catch (err) {
      console.error("Error in CurrentAffairViewer:", err);
      setContentError("An error occurred while trying to display this content.");
    }
  }, [extendedArticle, hasValidContent]);

  // Safeguard against null article
  if (!extendedArticle) {
    return (
      <Card className="p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>No article data available.</AlertDescription>
        </Alert>
      </Card>
    );
  }

  // Function to format date string correctly
  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return "";
    
    try {
      // Parse the date string (should be in YYYY-MM-DD format)
      const dateParts = dateString.split('-');
      if (dateParts.length !== 3) return dateString;
      
      // Create a new date using the parts (month is 0-indexed in JS Date)
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1; 
      const day = parseInt(dateParts[2]);
      
      const date = new Date(year, month, day);
      return date.toLocaleDateString();
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateString;
    }
  };

  return (
    <Card className={`${isMobile ? 'p-3' : 'p-6'} space-y-4 overflow-auto max-h-[600px]`}>
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>{extendedArticle.title || "Untitled Article"}</h2>
          {extendedArticle.date && (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              {formatDisplayDate(extendedArticle.date)}
              {extendedArticle.time_period && (
                <span className="capitalize">• {extendedArticle.time_period}</span>
              )}
              {extendedArticle.language && (
                <span className="uppercase">• {extendedArticle.language}</span>
              )}
            </div>
          )}
        </div>
        
        {/* Processing phase indicator */}
        {processingPhase === 'basic' && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex items-center gap-1 sm:gap-1.5">
            <Clock className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-blue-600 dark:text-blue-400" />
            <span className="text-[10px] sm:text-xs text-blue-700 dark:text-blue-300 font-medium">Basic View</span>
          </div>
        )}
        
        {processingPhase === 'enhanced' && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex items-center gap-1 sm:gap-1.5">
            <svg className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-[10px] sm:text-xs text-green-700 dark:text-green-300 font-medium">Enhanced View</span>
          </div>
        )}
      </div>

      <div className="prose prose-sm dark:prose-invert max-w-none">
        {!isContentLoaded ? (
          contentError ? (
            <Alert variant="destructive" className="my-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Content Issue</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>{contentError}</p>
                {(extendedArticle.processing_status === 'pending' || 
                  extendedArticle.processing_status === 'processing' ||
                  extendedArticle.content === "<p>Content is being processed</p>") && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={refreshArticleContent} 
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Checking Status...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" /> Check Status
                      </>
                    )}
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 space-y-2">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading content...</p>
            </div>
          )
        ) : (
          <>
            {/* Enhanced content warning if still in basic mode */}
            {processingPhase === 'basic' && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-600 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                      Enhanced processing in progress
                    </p>
                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                      You're viewing the basic version while enhanced content is being prepared. 
                      <Button 
                        variant="link" 
                        size="sm"
                        onClick={refreshArticleContent} 
                        disabled={isRefreshing}
                        className="text-blue-700 dark:text-blue-300 p-0 h-auto"
                      >
                        {isRefreshing ? 'Checking...' : 'Check again'}
                      </Button>
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div 
              dangerouslySetInnerHTML={{ __html: extendedArticle.content || "<p>No content available</p>" }} 
              className="space-y-4 
                      [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:mt-6 [&>h1]:mb-4
                      [&>h2]:text-xl [&>h2]:font-bold [&>h2]:mt-5 [&>h2]:mb-3
                      [&>h3]:text-lg [&>h3]:font-semibold [&>h3]:mt-5 [&>h3]:mb-3
                      [&>p]:my-3 [&>p]:leading-relaxed
                      [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:my-4 [&>ul]:space-y-2
                      [&>ol]:list-decimal [&>ol]:pl-5 [&>ol]:my-4 [&>ol]:space-y-2
                      [&_li]:ml-4 [&_li]:mb-1 [&_li]:list-item
                      [&_a]:text-blue-600 [&_a]:underline
                      [&_img]:max-w-full [&_img]:h-auto [&_img]:my-4 [&_img]:rounded-lg
                      [&_table]:w-full [&_table]:border-collapse [&_th]:bg-gray-100 [&_th]:p-2
                      [&_td]:border [&_td]:border-gray-300 [&_td]:p-2
                      [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic
                      [&_strong]:font-bold [&_em]:italic
                      [&_ul>li]:list-disc [&_ol>li]:list-decimal
                      [&_ul]:list-outside [&_ol]:list-outside"
            />
          </>
        )}
      </div>
    </Card>
  );
};

export { CurrentAffairViewer };
